import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatRelativeTime as fmtRelative, formatDateTime as fmtDateTime } from '../utils/time'
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
  arrayUnion,
  Timestamp
} from 'firebase/firestore'
import { db } from '../utils/firebase'
import {
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Send,
  Loader,
  Flag,
  MoreHorizontal,
  EyeOff,
  Pencil,
  Trash2,
  X,
  History
} from 'lucide-react'
import './DiscussionDetailPage.css'

function DiscussionDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { userProfile, isNeighborHidden, hideNeighbor } = useAuth()

  const isAdminRole = !!(userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin)

  const [discussion, setDiscussion] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [userVote, setUserVote] = useState(null) // null, 'up', or 'down'
  const [voting, setVoting] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [flagReason, setFlagReason] = useState('')
  const [flagging, setFlagging] = useState(false)
  const [flagSuccess, setFlagSuccess] = useState(false)
  const [hideSuccess, setHideSuccess] = useState(false)

  // Author / moderator affordances for the discussion itself.
  const [showEditDiscussionModal, setShowEditDiscussionModal] = useState(false)
  const [showDeleteDiscussionConfirm, setShowDeleteDiscussionConfirm] = useState(false)
  const [deletingDiscussion, setDeletingDiscussion] = useState(false)

  // Per-comment inline edit state — keyed by comment id.
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingCommentDraft, setEditingCommentDraft] = useState('')
  const [savingCommentEdit, setSavingCommentEdit] = useState(false)
  const [pendingCommentDelete, setPendingCommentDelete] = useState(null)
  const [deletingComment, setDeletingComment] = useState(false)
  const [historyComment, setHistoryComment] = useState(null)

  // Map of authorId (email) → profilePhotoURL, populated from the users
  // collection so the article, right-rail author card, and every comment
  // avatar all render the real photo instead of a fallback initial.
  const [authorPhotos, setAuthorPhotos] = useState({})

  // Sanitize email for use as Firebase field key (dots are interpreted as nested paths)
  const sanitizeEmail = (email) => email?.replace(/\./g, '_dot_') || ''

  useEffect(() => {
    loadDiscussion()
    loadComments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Build the email → photoURL map for everyone in the viewer's
  // neighborhood. One query per detail-page load; the map covers the
  // discussion author + every current or future commenter without
  // needing a per-row fetch.
  useEffect(() => {
    if (!userProfile?.neighborhoodId) return
    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'users'),
          where('neighborhoodId', '==', userProfile.neighborhoodId)
        ))
        if (cancelled) return
        const map = {}
        snap.docs.forEach(d => {
          const u = d.data() || {}
          if (u.email && u.profilePhotoURL) map[u.email] = u.profilePhotoURL
        })
        setAuthorPhotos(map)
      } catch (e) {
        console.warn('Could not load author photos:', e)
      }
    })()
    return () => { cancelled = true }
  }, [userProfile?.neighborhoodId])

  const loadDiscussion = async () => {
    try {
      const docRef = doc(db, 'discussions', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        setDiscussion({
          id: docSnap.id,
          ...data,
          createdDate: data.createdDate?.toDate() || new Date(),
          lastActivity: data.lastActivity?.toDate() || new Date()
        })

        const emailKey = sanitizeEmail(userProfile?.email)
        if (emailKey && data.votedUsers?.[emailKey] !== undefined) {
          setUserVote(data.votedUsers[emailKey] ? 'up' : 'down')
        }
      } else {
        navigate('/discussions')
      }
    } catch (error) {
      console.error('Error loading discussion:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadComments = async () => {
    try {
      const commentsQuery = query(
        collection(db, 'comments'),
        where('discussionId', '==', id),
        orderBy('createdDate', 'asc')
      )

      const snapshot = await getDocs(commentsQuery)
      const commentsList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdDate: d.data().createdDate?.toDate() || new Date()
      }))

      setComments(commentsList)
    } catch (error) {
      console.error('Error loading comments:', error)
    }
  }

  const handleVote = async (isUpvote) => {
    if (!userProfile || voting) return

    setVoting(true)
    const docRef = doc(db, 'discussions', id)

    try {
      const currentVote = userVote
      let upvoteChange = 0
      let downvoteChange = 0
      let newVote = null

      if (isUpvote) {
        if (currentVote === 'up') { upvoteChange = -1; newVote = null }
        else if (currentVote === 'down') { upvoteChange = 1; downvoteChange = -1; newVote = 'up' }
        else { upvoteChange = 1; newVote = 'up' }
      } else {
        if (currentVote === 'down') { downvoteChange = -1; newVote = null }
        else if (currentVote === 'up') { upvoteChange = -1; downvoteChange = 1; newVote = 'down' }
        else { downvoteChange = 1; newVote = 'down' }
      }

      const emailKey = sanitizeEmail(userProfile.email)
      const updates = {
        upvotes: increment(upvoteChange),
        downvotes: increment(downvoteChange),
        [`votedUsers.${emailKey}`]: newVote === 'up' ? true : newVote === 'down' ? false : null
      }

      await updateDoc(docRef, updates)

      setUserVote(newVote)
      setDiscussion(prev => ({
        ...prev,
        upvotes: (prev.upvotes || 0) + upvoteChange,
        downvotes: (prev.downvotes || 0) + downvoteChange
      }))
    } catch (error) {
      console.error('Error voting:', error)
    } finally {
      setVoting(false)
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() || submitting) return

    setSubmitting(true)

    try {
      await addDoc(collection(db, 'comments'), {
        discussionId: id,
        content: newComment.trim(),
        authorId: userProfile.email,
        authorFirstName: userProfile.firstName,
        authorStreetAddress: userProfile.streetAddress,
        createdDate: serverTimestamp()
      })

      await updateDoc(doc(db, 'discussions', id), {
        messageCount: increment(1),
        lastActivity: serverTimestamp()
      })

      setNewComment('')
      loadComments()
      setDiscussion(prev => ({
        ...prev,
        messageCount: (prev.messageCount || 0) + 1
      }))
    } catch (error) {
      console.error('Error posting comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFlagPost = async () => {
    if (!userProfile || flagging) return
    setFlagging(true)
    try {
      await addDoc(collection(db, 'content_flags'), {
        contentId: discussion.id,
        contentType: 'post',
        postId: discussion.id,
        reporterId: userProfile.email,
        reporterName: userProfile.firstName || 'Anonymous',
        reason: flagReason.trim() || 'Inappropriate content',
        status: 'pending',
        resolved: false,
        createdAt: serverTimestamp(),
        neighborhoodId: userProfile.neighborhoodId,
        contentPreview: discussion.content?.substring(0, 200) || discussion.title
      })
      setFlagSuccess(true)
      setShowFlagModal(false)
      setFlagReason('')
      setTimeout(() => setFlagSuccess(false), 3000)
    } catch (error) {
      console.error('Error flagging post:', error)
    } finally {
      setFlagging(false)
    }
  }

  // Save an edit to the discussion (title / content / category). Called
  // from the edit modal below. `updatedAt` and `editedBy*` stamps let us
  // display a Facebook-style "edited" note if we choose to later.
  const handleSaveDiscussionEdit = async ({ title, content, category }) => {
    if (!discussion) return
    await updateDoc(doc(db, 'discussions', discussion.id), {
      title: title.trim(),
      content: content.trim(),
      category,
      updatedAt: serverTimestamp(),
      editedById: userProfile.email,
      editedByName: userProfile.firstName || 'Anonymous'
    })
    setDiscussion(prev => ({
      ...prev,
      title: title.trim(),
      content: content.trim(),
      category
    }))
    setShowEditDiscussionModal(false)
  }

  // Delete a discussion AND every comment on it. Mirrors the admin
  // Manage Discussions cascade (see AdminPage.jsx) so behavior stays
  // consistent whether an admin or the author performs the delete.
  const handleDeleteDiscussion = async () => {
    if (!discussion || deletingDiscussion) return
    setDeletingDiscussion(true)
    try {
      const commentsSnap = await getDocs(
        query(collection(db, 'comments'), where('discussionId', '==', discussion.id))
      )
      await Promise.all(commentsSnap.docs.map(d => deleteDoc(d.ref)))
      await deleteDoc(doc(db, 'discussions', discussion.id))
      navigate('/discussions')
    } catch (error) {
      console.error('Error deleting discussion:', error)
      setDeletingDiscussion(false)
      setShowDeleteDiscussionConfirm(false)
    }
  }

  const startEditingComment = (comment) => {
    setEditingCommentId(comment.id)
    setEditingCommentDraft(comment.content || '')
  }

  const cancelEditingComment = () => {
    setEditingCommentId(null)
    setEditingCommentDraft('')
  }

  // Save an edit to a comment. Appends the previous content to
  // `editHistory` (an array of { content, editedAt, editedBy,
  // editedByName } entries, newest last) so we can render a
  // Facebook-style edit history. `Timestamp.now()` is used inside the
  // array because `serverTimestamp()` isn't valid inside arrays.
  const saveCommentEdit = async (comment) => {
    const draft = editingCommentDraft.trim()
    if (!draft || savingCommentEdit) return
    setSavingCommentEdit(true)
    try {
      const historyEntry = {
        content: comment.content || '',
        editedAt: Timestamp.now(),
        editedBy: userProfile.email,
        editedByName: userProfile.firstName || 'Anonymous'
      }
      await updateDoc(doc(db, 'comments', comment.id), {
        content: draft,
        updatedAt: serverTimestamp(),
        editHistory: arrayUnion(historyEntry)
      })
      setComments(prev => prev.map(c => c.id === comment.id
        ? {
            ...c,
            content: draft,
            editHistory: [...(c.editHistory || []), historyEntry]
          }
        : c
      ))
      cancelEditingComment()
    } catch (error) {
      console.error('Error saving comment edit:', error)
    } finally {
      setSavingCommentEdit(false)
    }
  }

  const confirmDeleteComment = async () => {
    if (!pendingCommentDelete || deletingComment) return
    const commentId = pendingCommentDelete.id
    setDeletingComment(true)
    try {
      await deleteDoc(doc(db, 'comments', commentId))
      await updateDoc(doc(db, 'discussions', id), {
        messageCount: increment(-1)
      }).catch(() => {}) // non-fatal if count already off
      setComments(prev => prev.filter(c => c.id !== commentId))
      setDiscussion(prev => prev ? {
        ...prev,
        messageCount: Math.max(0, (prev.messageCount || 1) - 1)
      } : prev)
      setPendingCommentDelete(null)
    } catch (error) {
      console.error('Error deleting comment:', error)
    } finally {
      setDeletingComment(false)
    }
  }

  const { settings } = useAppSettings()
  const formatRelativeTime = (date) => fmtRelative(date, settings)
  const formatFullDateTime = (date) => (date ? fmtDateTime(date, settings) : '')

  const filteredComments = comments.filter(c => !isNeighborHidden(c.authorId))

  if (loading) {
    return (
      <div className="discussion-detail-loading">
        <div className="spinner"></div>
        <p className="muted">Loading discussion…</p>
      </div>
    )
  }

  if (!discussion) {
    return (
      <div className="discussion-not-found">
        <h2>Discussion not found</h2>
        <Link to="/discussions" className="btn-outline">Back to Discussions</Link>
      </div>
    )
  }

  const netVotes = (discussion.upvotes || 0) - (discussion.downvotes || 0)
  const authorInitial = (discussion.authorFirstName || 'A').charAt(0).toUpperCase()
  const viewerEmail = userProfile?.email || ''
  const isOwnPost = !!(discussion.authorId && viewerEmail && discussion.authorId === viewerEmail)
  const canManageDiscussion = isOwnPost || isAdminRole

  return (
    <div className="detail-layout discussion-detail">
      {/* CENTER — article + comments. */}
      <div className="discussion-detail-main">
        <Link to="/discussions" className="btn-ghost discussion-back">
          <ArrowLeft size={18} />
          <span>All discussions</span>
        </Link>
        <article className="discussion-article card">
          <div className="discussion-article-topmeta">
            <span className="badge discussion-article-badge">{discussion.category || 'General'}</span>
            {/* Post actions menu.
              *
              * Owners (and admin/board/super-admin moderators) see Edit
              * and Delete. Non-owners see Flag / Hide. If the viewer is
              * both the author AND an admin, we only surface Edit /
              * Delete once — no double-up.
              */}
            {(canManageDiscussion || !isOwnPost) && (
              <div className="post-menu-wrapper">
                <button
                  type="button"
                  className="btn-ghost post-menu-btn"
                  onClick={() => setShowMenu(!showMenu)}
                  onBlur={() => setTimeout(() => setShowMenu(false), 150)}
                  aria-label="Post actions"
                  data-walk="discussion-menu-btn"
                >
                  <MoreHorizontal size={20} />
                </button>
                {showMenu && (
                  <div className="post-menu-dropdown">
                    {canManageDiscussion && (
                      <>
                        <button type="button" onClick={() => { setShowMenu(false); setShowEditDiscussionModal(true) }}>
                          <Pencil size={16} />
                          Edit discussion
                        </button>
                        <button type="button" onClick={() => { setShowMenu(false); setShowDeleteDiscussionConfirm(true) }}>
                          <Trash2 size={16} />
                          Delete discussion
                        </button>
                      </>
                    )}
                    {!isOwnPost && (
                      <>
                        <button type="button" onClick={() => { setShowMenu(false); setShowFlagModal(true) }}>
                          <Flag size={16} />
                          Flag as inappropriate
                        </button>
                        <button type="button" onClick={() => {
                          setShowMenu(false)
                          hideNeighbor(discussion.authorId)
                          setHideSuccess(true)
                          setTimeout(() => setHideSuccess(false), 3000)
                        }}>
                          <EyeOff size={16} />
                          Hide neighbor
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <h1 className="discussion-article-title">{discussion.title}</h1>

          <div className="discussion-article-author">
            <div className="avatar" aria-hidden="true">
              {(authorPhotos[discussion.authorId] || discussion.authorPhotoURL)
                ? <img src={authorPhotos[discussion.authorId] || discussion.authorPhotoURL} alt="" />
                : authorInitial}
            </div>
            <div className="discussion-article-author-text">
              <span className="discussion-article-author-name">
                {discussion.authorFirstName || 'Anonymous'}
                {discussion.authorStreetAddress && ` · ${discussion.authorStreetAddress}`}
              </span>
              <span className="muted discussion-article-author-time">
                {formatRelativeTime(discussion.createdDate)}
              </span>
            </div>
          </div>

          {discussion.imageURL && (
            <div className="discussion-article-image">
              <img src={discussion.imageURL} alt="" />
            </div>
          )}

          {discussion.content && (
            <div className="discussion-article-content">
              <p>{discussion.content}</p>
            </div>
          )}

          {/* Voting — compact pill-buttons at the article footer. */}
          <div className="voting-section">
            <button
              type="button"
              className={`btn-outline vote-btn${userVote === 'up' ? ' active' : ''}`}
              onClick={() => handleVote(true)}
              disabled={voting}
            >
              <ThumbsUp size={18} />
              <span>{discussion.upvotes || 0}</span>
            </button>

            <button
              type="button"
              className={`btn-outline vote-btn${userVote === 'down' ? ' active down' : ''}`}
              onClick={() => handleVote(false)}
              disabled={voting}
            >
              <ThumbsDown size={18} />
              <span>{discussion.downvotes || 0}</span>
            </button>

            <span className={`vote-total${netVotes > 0 ? ' positive' : netVotes < 0 ? ' negative' : ''}`}>
              {netVotes > 0 ? `+${netVotes}` : netVotes}
            </span>
          </div>
        </article>

        {/* Comments */}
        <section className="comments-section">
          <h2 className="comments-heading">
            <MessageSquare size={18} />
            {filteredComments.length} {filteredComments.length === 1 ? 'comment' : 'comments'}
          </h2>

          {/* Composer — sticks to viewport bottom on tall screens. */}
          <form className="comment-form card" onSubmit={handleSubmitComment}>
            <div className="comment-form-row">
              <div className="avatar sm" aria-hidden="true">
                {userProfile?.photoURL
                  ? <img src={userProfile.photoURL} alt="" />
                  : (userProfile?.firstName || 'Y').charAt(0).toUpperCase()}
              </div>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment…"
                rows={2}
              />
            </div>
            <div className="comment-form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={!newComment.trim() || submitting}
              >
                {submitting ? <Loader size={16} className="spinner" /> : <Send size={16} />}
                <span>Post</span>
              </button>
            </div>
          </form>

          <div className="comments-list">
            {filteredComments.length === 0 ? (
              <div className="card no-comments">
                <p className="muted">No comments yet. Be the first to share your thoughts.</p>
              </div>
            ) : (
              filteredComments.map(comment => {
                const isOwnComment = !!(comment.authorId && viewerEmail && comment.authorId === viewerEmail)
                const canManageComment = isOwnComment || isAdminRole
                const isEditing = editingCommentId === comment.id
                const editHistory = Array.isArray(comment.editHistory) ? comment.editHistory : []
                const hasHistory = editHistory.length > 0
                return (
                  <div key={comment.id} className="comment card">
                    <div className="avatar" aria-hidden="true">
                      {(authorPhotos[comment.authorId] || comment.authorPhotoURL)
                        ? <img src={authorPhotos[comment.authorId] || comment.authorPhotoURL} alt="" />
                        : (comment.authorFirstName || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div className="comment-body">
                      <div className="comment-header">
                        <span className="comment-author">
                          {comment.authorFirstName || 'Anonymous'}
                          {comment.authorStreetAddress && ` · ${comment.authorStreetAddress}`}
                        </span>
                        <span className="muted comment-time">
                          {formatRelativeTime(comment.createdDate)}
                        </span>
                        {canManageComment && !isEditing && (
                          <span className="comment-actions">
                            <button
                              type="button"
                              className="btn-ghost comment-action-btn"
                              onClick={() => startEditingComment(comment)}
                              aria-label="Edit comment"
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn-ghost comment-action-btn"
                              onClick={() => setPendingCommentDelete(comment)}
                              aria-label="Delete comment"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="comment-edit">
                          <textarea
                            value={editingCommentDraft}
                            onChange={(e) => setEditingCommentDraft(e.target.value)}
                            rows={3}
                          />
                          <div className="comment-edit-actions">
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={cancelEditingComment}
                              disabled={savingCommentEdit}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => saveCommentEdit(comment)}
                              disabled={savingCommentEdit || !editingCommentDraft.trim()}
                            >
                              {savingCommentEdit ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="comment-content">{comment.content}</p>
                          {hasHistory && (
                            <button
                              type="button"
                              className="comment-edit-pill"
                              onClick={() => setHistoryComment(comment)}
                            >
                              <History size={12} />
                              Edited · view history
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </section>
      </div>

      {/* Edit Discussion Modal */}
      {showEditDiscussionModal && (
        <EditDiscussionModal
          discussion={discussion}
          onClose={() => setShowEditDiscussionModal(false)}
          onSave={handleSaveDiscussionEdit}
        />
      )}

      {/* Delete Discussion Confirm */}
      {showDeleteDiscussionConfirm && (
        <div className="modal-overlay" onClick={() => !deletingDiscussion && setShowDeleteDiscussionConfirm(false)}>
          <div className="flag-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete this discussion?</h3>
            <p className="muted">
              This will permanently remove the discussion and every reply on it. This cannot be undone.
            </p>
            <div className="flag-modal-actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowDeleteDiscussionConfirm(false)}
                disabled={deletingDiscussion}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleDeleteDiscussion}
                disabled={deletingDiscussion}
              >
                {deletingDiscussion ? 'Deleting…' : 'Delete discussion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Confirm */}
      {pendingCommentDelete && (
        <div className="modal-overlay" onClick={() => !deletingComment && setPendingCommentDelete(null)}>
          <div className="flag-modal" onClick={e => e.stopPropagation()}>
            <h3>Delete this comment?</h3>
            <p className="muted">
              This will permanently remove the comment. This cannot be undone.
            </p>
            <div className="flag-modal-actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setPendingCommentDelete(null)}
                disabled={deletingComment}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={confirmDeleteComment}
                disabled={deletingComment}
              >
                {deletingComment ? 'Deleting…' : 'Delete comment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit history modal — reverse-chronological, Facebook-style. */}
      {historyComment && (
        <div className="modal-overlay" onClick={() => setHistoryComment(null)}>
          <div className="modal edit-history-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit history</h2>
              <button type="button" className="btn-ghost close-btn" onClick={() => setHistoryComment(null)} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="edit-history-body">
              <div className="edit-history-entry current">
                <div className="edit-history-meta">
                  <strong>Current version</strong>
                </div>
                <p className="edit-history-content">{historyComment.content}</p>
              </div>
              {[...(historyComment.editHistory || [])]
                .slice()
                .reverse()
                .map((entry, idx) => {
                  const editedAt = entry.editedAt?.toDate?.() || null
                  return (
                    <div key={idx} className="edit-history-entry">
                      <div className="edit-history-meta muted">
                        Edited by {entry.editedByName || entry.editedBy || 'Someone'}
                        {editedAt ? ` · ${formatFullDateTime(editedAt)}` : ''}
                      </div>
                      <p className="edit-history-content">{entry.content}</p>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="modal-overlay" onClick={() => setShowFlagModal(false)}>
          <div className="flag-modal" onClick={e => e.stopPropagation()}>
            <h3>Flag content</h3>
            <p className="muted">
              This will be reported to administrators for review. Add a brief reason if helpful.
            </p>
            <textarea
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
            />
            <div className="flag-modal-actions">
              <button type="button" className="btn-outline" onClick={() => { setShowFlagModal(false); setFlagReason('') }}>
                Cancel
              </button>
              <button type="button" className="btn-primary danger" onClick={handleFlagPost} disabled={flagging}>
                {flagging ? 'Flagging…' : 'Flag as inappropriate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toasts */}
      {flagSuccess && (
        <div className="flag-toast">
          <Flag size={16} />
          Content flagged. Administrators will review it shortly.
        </div>
      )}
      {hideSuccess && (
        <div className="flag-toast">
          <EyeOff size={16} />
          Neighbor hidden. Manage hidden neighbors from your Profile.
        </div>
      )}
    </div>
  )
}

// Edit Discussion Modal — replicates the create form in edit mode.
// Kept local to this file since it only edits the currently open
// discussion; extract later if another surface needs it.
function EditDiscussionModal({ discussion, onClose, onSave }) {
  const [title, setTitle] = useState(discussion.title || '')
  const [content, setContent] = useState(discussion.content || '')
  const [category, setCategory] = useState(discussion.category || 'General')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [categories, setCategories] = useState(null)

  // Fetch this neighborhood's category list so the dropdown mirrors
  // what the create modal offers. Falls back to the current value if
  // the neighborhood doc doesn't customize categories.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!discussion.neighborhoodId) return
        const snap = await getDoc(doc(db, 'neighborhoods', discussion.neighborhoodId))
        if (!cancelled && snap.exists()) {
          const data = snap.data()
          if (Array.isArray(data.discussionCategories) && data.discussionCategories.length > 0) {
            setCategories(data.discussionCategories)
          }
        }
      } catch (e) {
        // Non-fatal — the field is still editable via free-form fallback.
      }
    })()
    return () => { cancelled = true }
  }, [discussion.neighborhoodId])

  const options = categories && categories.length > 0
    ? categories
    : [category, 'General'].filter((v, i, a) => a.indexOf(v) === i)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Please enter a title')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave({ title, content, category })
    } catch (err) {
      console.error('Error saving discussion edit:', err)
      setError('Failed to save changes. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit discussion</h2>
          <button type="button" className="btn-ghost close-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="edit-title">Title</label>
            <input
              id="edit-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-category">Category</label>
            <select
              id="edit-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {options.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit-content">Details</label>
            <textarea
              id="edit-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-outline" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader size={18} className="spinner" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DiscussionDetailPage
